/**
 * Workspace API Hooks
 *
 * Re-exports workspace hooks from the canonical useDashboard module
 * for feature-based organization consistency.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

// Re-export canonical workspace hooks
export { useWorkspaces, useWorkspace } from '@/hooks/useDashboard';
export type { Workspace, WorkspaceWithStats } from '@/hooks/useDashboard';

/**
 * Workspace mutation types
 */
interface CreateWorkspaceData {
  name: string;
  slug?: string;
}

interface UpdateWorkspaceData {
  name?: string;
  description?: string;
}

interface WorkspaceResponse {
  workspace: {
    id: string;
    name: string;
    slug: string;
    ownerId: string;
    createdAt: string;
    updatedAt: string;
  };
}

/**
 * Create a new workspace
 */
export function useCreateWorkspace() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateWorkspaceData): Promise<WorkspaceResponse['workspace']> => {
      const response = await api.post<WorkspaceResponse>('/workspaces', data);
      return response.workspace;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
    },
  });
}

/**
 * Update an existing workspace
 */
export function useUpdateWorkspace() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateWorkspaceData }): Promise<WorkspaceResponse['workspace']> => {
      const response = await api.patch<WorkspaceResponse>(`/workspaces/${id}`, data);
      return response.workspace;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      queryClient.invalidateQueries({ queryKey: ['workspace', variables.id] });
    },
  });
}

/**
 * Delete a workspace
 */
export function useDeleteWorkspace() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await api.delete(`/workspaces/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
    },
  });
}
